import { useState } from "react";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  ButtonBase,
  Checkbox,
  Menu,
  MenuItem,
  TableCell,
  Typography,
  useTheme,
} from "@mui/material";

export type FilterOption<Value extends string = string> = {
  value: Value;
  label: string;
};

export type FilterableColumnHeaderProps<Value extends string = string> = {
  label: string;
  options: readonly FilterOption<Value>[];
  selected: readonly Value[];
  onSelectionChange: (selected: Value[]) => void;
};

export function FilterableColumnHeader<Value extends string>({
  label,
  options,
  selected,
  onSelectionChange,
}: FilterableColumnHeaderProps<Value>) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isFiltered = selected.length > 0;

  const colour = isFiltered
    ? theme.palette.primary.main
    : theme.palette.text.primary;

  if (options.length === 0 && selected.length === 0) {
    return <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>;
  }

  return (
    <TableCell>
      <ButtonBase
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.25em",
          color: colour,
          fontWeight: 700,
          fontSize: "0.875rem",
          borderRadius: "4px",
          padding: "0.1em 0.2em",
        }}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        {label}
        <KeyboardArrowDown sx={{ fontSize: "1.1rem" }} />
        {isFiltered && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "1.4em",
              height: "1.4em",
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontSize: "0.7rem",
              fontWeight: 700,
            }}
          >
            {selected.length}
          </Box>
        )}
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => {
          setAnchorEl(null);
        }}
        MenuListProps={{ "aria-label": `Filter by ${label.toLowerCase()}` }}
        slotProps={{
          paper: { sx: { borderRadius: "12px", minWidth: "13em" } },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: theme.palette.text.secondary,
            padding: "0.5em 1.15em",
          }}
        >
          FILTER
        </Typography>

        {options.map((option) => {
          const isSelected = selected.includes(option.value);

          return (
            <MenuItem
              key={option.value}
              sx={{ gap: "0.5em" }}
              onClick={() => {
                onSelectionChange(
                  isSelected
                    ? selected.filter((value) => value !== option.value)
                    : [...selected, option.value],
                );
              }}
            >
              <Checkbox
                checked={isSelected}
                size="small"
                disableRipple
                sx={{ padding: 0 }}
                inputProps={{ "aria-label": option.label }}
              />
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
    </TableCell>
  );
}
